import { ProblemType } from '@core/models/api/problem.api.model';
import { UserStatus } from '@core/models/user-status.model';

/**
 * What a signed-in user whose account is not ENABLED should be told.
 *
 * - `invited`: an invitation email was sent; the user has to accept it
 * - `imported`: migrated from Keyring, never invited; an owner or admin has to send an invitation
 * - `inactive`: disabled, archived, locked or expired; only a workspace owner can help
 * - `unknown`: the status cannot be determined: no claim, an unrecognised value, or an ENABLED
 * account that still has no workspace to land in and was not refused by the API
 */
export type AccountState = 'invited' | 'imported' | 'inactive' | 'unknown';

const STATE_BY_STATUS: Record<Exclude<UserStatus, 'STATUS_ENABLED'>, AccountState> = {
    /* eslint-disable @typescript-eslint/naming-convention -- mirrors the backend enum */
    STATUS_INVITED: 'invited',
    STATUS_IMPORTED: 'imported',
    STATUS_DISABLED: 'inactive',
    STATUS_ARCHIVED: 'inactive',
    STATUS_LOCKED: 'inactive',
    STATUS_EXPIRED: 'inactive',
    /* eslint-enable @typescript-eslint/naming-convention */
};

/** The API refused the request because of the account's status, not because of an outage or a bad session. */
export function isAccountStatusProblem(problem: string | undefined): boolean {
    return problem === ProblemType.ACCOUNT_NOT_ACTIVE || problem === ProblemType.ACCOUNT_STATUS_UNKNOWN;
}

/**
 * Picks the state from the token's status claim; when the token carries none, from the problem
 * type the API answered with (`account-not-active` says the account exists but is not active).
 *
 * An ENABLED claim cannot explain why the user is here, so it defers to the problem type too:
 * the claim is set at login and goes stale when the account is disabled afterwards, since a
 * refresh-token exchange does not run the Auth0 Action again.
 */
export function accountStateOf(status: UserStatus | null | undefined, problem?: string): AccountState {
    if (status && status !== 'STATUS_ENABLED') {
        return STATE_BY_STATUS[status];
    }

    return problem === ProblemType.ACCOUNT_NOT_ACTIVE ? 'inactive' : 'unknown';
}
